import get from 'lodash.get';
import forEach from 'lodash.foreach';
import filter from 'lodash.filter';
import concat from 'lodash.concat';
import map from 'lodash.map';
import keys from 'lodash.keys';
import pickBy from 'lodash.pickby';
import auctionLightTypes from '@/constants/auctionLightTypes';
import * as auctionStates from '@/constants/auctionStates';
import placeholderImage from '../assets/placeholder.png';
import ConditionReport from './ConditionReport';

export default class BaseAuction {
  constructor(auction) {
    this.auction = auction;

    if (!(this.auction.condition_report instanceof ConditionReport)) {
      this.auction.condition_report = new ConditionReport(
        auction.auction_condition_report,
        auction.condition_report,
      );
    }
    this.placeholderImage = placeholderImage;
  }

  get id() {
    return get(this.auction, 'id');
  }

  get autosell() {
    return get(this.auction, 'autosell');
  }

  get finalized() {
    return this.auction.post_auction_data?.finalized || this.auction.finalized || false;
  }

  get status() {
    const status = get(this.auction, 'status', '');
    return typeof (status) === 'object' ? status.value : status.toLowerCase();
  }

  set status(status) {
    this.auction.status = status;
    /* eslint-disable-next-line */ // look into removing the return
    return this.status;
  }

  get floorPrice() {
    return get(this.auction, 'floor_price', 0) || 0;
  }

  set floorPrice(floorPrice) {
    this.auction.floor_price = floorPrice;
  }

  get make() {
    // eslint-disable-next-line camelcase
    return this.auction?.make || this.auction?.vehicle_make || '';
  }

  get model() {
    // eslint-disable-next-line camelcase
    let model = this.auction?.model || this.auction?.vehicle_model || '';
    if (model.toLowerCase().indexOf('powersports') !== -1
      || model.toLowerCase().indexOf('commercial') !== -1) {
      model = get(this.auction, 'trim', '');
    }
    return model;
  }

  get trim() {
    return get(this.auction, 'trim', '');
  }

  get vehicleStyle() {
    return get(this.auction, 'style', '');
  }

  get year() {
    // eslint-disable-next-line camelcase
    return this.auction?.year || this.auction?.vehicle_year || '';
  }

  get doors() {
    return get(this.auction, 'doors', '');
  }

  get drivetrain() {
    return get(this.auction, 'drivetrain', '');
  }

  get cylinders() {
    return get(this.auction, 'cylinders', '');
  }

  get transmission() {
    return get(this.auction, 'transmission', '');
  }

  get orderCode() {
    return get(this.auction, 'order_code', '');
  }

  get odometer() {
    return get(this.auction, 'odometer', 0);
  }

  get recommendedPrice() {
    return get(this.auction, 'recommended_price');
  }

  get recommendedPriceSellerNotes() {
    return get(this.auction, 'recommendedPrice.seller_notes', '');
  }

  get recommendedPriceFloorFormatted() {
    return get(this.auction, 'recommendedPrice.floor.formatted', '');
  }

  get recommendedPriceCeilingFormatted() {
    return get(this.auction, 'recommendedPrice.ceiling.formatted', '');
  }

  get reservePricing() {
    return get(this.auction, 'reserve_pricing', {}) || {};
  }

  get hasRequiredReserve() {
    return get(this, 'reservePricing.reserve.required', false);
  }

  get type() {
    return get(this.auction, 'type');
  }

  set type(value) {
    this.auction.type = value;
  }

  get sellerDealerId() {
    return get(this.auction, 'seller.dealer.id');
  }

  get proxyBid() {
    return get(this.auction, 'proxy_bid');
  }

  set proxyBid(proxyBid) {
    this.auction.proxy_bid = proxyBid;
  }

  get proxyBidAmount() {
    return get(this, 'proxyBid.amount');
  }

  set proxyBidAmount(proxyBidAmount) {
    this.auction.proxy_bid.amount = parseInt(proxyBidAmount, 10);
  }

  get proxyBidIsPersistent() {
    return get(this, 'proxyBid.is_persistent', false);
  }

  set proxyBidIsPersistent(proxyBidIsPersistent) {
    this.auction.proxy_bid.is_persistent = proxyBidIsPersistent;
  }

  get proxyBidInterval() {
    return get(this.auction, 'proxy_bid_interval', 50);
  }

  get sellerBadgeInfo() {
    return get(this.auction, 'seller_badge_info');
  }

  get featuredSeller() {
    return get(this.auction, 'seller_status.featured_seller', {});
  }

  get topSeller() {
    return get(this.auction, 'seller_status.top_seller', {});
  }

  get sellerDealerName() {
    return get(this.auction, 'seller.dealer.name');
  }

  get vehicleDisplayName() {
    return get(this.auction, 'vehicle_display_name', this.vehicleName);
  }

  get vehicleName() {
    return get(this.auction, 'vehicle_name', '');
  }

  get vin() {
    return get(this.auction, 'vin', '');
  }

  get images() {
    const images = get(this.auction, 'images', []);
    return images.length ? images : [
      {
        image_key: 'placeholder',
        url: this.primaryImageMedium,
        thumbnail: this.photoThumbnail,
        acv_thumbnail: this.photoThumbnail,
        acv_medium: this.primaryImageMedium,
        acv_large: this.primaryImageLarge,
      },
    ];
  }

  set images(images) {
    this.auction.images = images;
  }

  get isActive() {
    return this.status === 'active';
  }

  get highBid() {
    return get(this.auction, 'high_bid');
  }

  get highBidAmount() {
    return get(this.auction, 'high_bid.amount', 0) || 0;
  }

  get isAutoSell() {
    return get(this.auction, 'autosell', false);
  }

  get photoCount() {
    return this.images.length;
  }

  get primaryImage() {
    return get(this.auction, 'primary_image');
  }

  get primaryImageMedium() {
    /* eslint-disable-next-line camelcase */
    return this.auction?.primary_image?.acv_medium || this.placeholderImage;
  }

  get primaryImageLarge() {
    /* eslint-disable-next-line camelcase */
    return this.auction?.primary_image?.acv_large || this.primaryImageMedium;
  }

  get photoThumbnail() {
    return this.auction.acv_thumbnail
      || get(this.auction, 'primary_image.acv_thumbnail')
      || this.placeholderImage;
  }

  get sellerUserId() {
    return get(this.auction, 'seller.user.id');
  }

  set sellerUserId(sellerUserId) {
    this.auction.seller.user.id = this.validateNumber(sellerUserId);
  }

  isPrimarySeller(user) {
    return user.id === this.sellerUserId;
  }

  get auctionLights() {
    const types = map(auctionLightTypes, 'value');
    const auctionLights = keys(pickBy(this.auction, (value, prop) => types
      .includes(prop) && value === true));

    return auctionLights;
  }

  get isGreenLight() {
    return this.greenLight === true;
  }

  get greenLight() {
    return get(this.auction, 'green_light');
  }

  get isYellowLight() {
    return this.yellowLight === true;
  }

  get yellowLight() {
    return get(this.auction, 'yellow_light');
  }

  get blueLight() {
    return get(this.auction, 'blue_light');
  }

  get isSellable() {
    return get(this.auction, 'sellable', false);
  }

  get inAcceptedState() {
    return auctionStates.acceptedStates.includes(this.status);
  }

  get inNegotiationAcceptedState() {
    return auctionStates.negotiationAcceptedStates.includes(this.status);
  }

  get inNegotiationUnsoldState() {
    return auctionStates.negotiationUnsoldStates.includes(this.status);
  }

  get inNegotiationState() {
    return auctionStates.negotiationStates.includes(this.status);
  }

  get inUnsoldState() {
    return auctionStates.unsoldStates.includes(this.status);
  }

  get inEndedNoParticipationState() {
    return auctionStates.endedNoParticipationStates.includes(this.status);
  }

  get inFinalState() {
    return auctionStates.finalStates.includes(this.status);
  }

  get inEndedState() {
    return auctionStates.endedStates.includes(this.status);
  }

  get inMakeOfferState() {
    return auctionStates.makeOfferStates.includes(this.status);
  }

  get inRelaunchableState() {
    return auctionStates.relaunchableStates.includes(this.status);
  }

  /* eslint-disable-next-line */
  get hasPendingOffer() {
    return this.auction.pendingOffer || false;
  }

  // TODO: Wire up to actual value - ETC
  /* eslint-disable-next-line */
  set hasPendingOffer(offer) {
    this.auction.pendingOffer = offer;
  }

  // eslint-disable-next-line
  get isParticipant() {
    return false;
  }

  get isAwaitingAcceptance() {
    return this.auction.status === 'awaiting_acceptance';
  }

  get isRunList() {
    return this.auction.status === 'run_list';
  }

  get isPreview() {
    return this.auction.status === 'preview';
  }

  // eslint-disable-next-line
  validateNumber(value) {
    const valueNumber = parseInt(value, 10);
    if (Number.isNaN(valueNumber)) {
      throw new Error('Tried setting a value that was NaN!');
    }
    return valueNumber;
  }

  get notes() {
    return get(this.auction, 'notes', '') || '';
  }

  get conditionReport() {
    return get(this.auction, 'condition_report', { sections: [] });
  }

  set conditionReport(conditionReport) {
    this.auction.condition_report = conditionReport;
  }

  get announcements() {
    let announcementQuestions = [];

    forEach(this.conditionReport.sections, (section) => {
      const selectedQuestions = filter(section.questions, { selected: true });
      announcementQuestions = concat(announcementQuestions, selectedQuestions);
    });

    return announcementQuestions;
  }

  get displayInfo() {
    return get(this.auction, 'display_info', []);
  }

  get loading() {
    return get(this.auction, 'loading');
  }

  set loading(loading) {
    this.auction.loading = loading;
  }

  get carfaxAlerts() {
    return get(this.auction, 'carfax_alerts');
  }

  set carfaxAlerts(carfaxAlerts) {
    this.auction.carfax_alerts = carfaxAlerts;
  }

  get alert() {
    return get(this.auction, 'auction_alert', '');
  }

  get alertMessage() {
    return get(this.auction, 'auction_alert_text', '') || get(this.auction, 'alert_message');
  }

  get auctionAction() {
    return get(this.auction, 'auction_action');
  }

  get auctionActionText() {
    return get(this.auction, 'auction_action_text', '');
  }

  get startTime() {
    return get(this.auction, 'start_time');
  }

  get snapshotId() {
    return get(this.auction, 'snapshot_id');
  }

  get isInOp() {
    return this.conditionReport?.sections
      ?.find((s) => s.section.section_name.toLowerCase() === 'driveability')?.questions
      ?.find((q) => q.question_key === 'vehicle_inop').answer.value
      .toLowerCase() === 'yes';
  }

  get statusPills() {
    return get(this.auction, 'status_pills', []);
  }

  set statusPills(pills) {
    this.auction.status_pills = pills || [];
  }

  get canKill() {
    return get(this.auction, 'can_kill', false);
  }

  get auctionCRtemplate() {
    return get(this.auction, 'auction_condition_report.template_name', '');
  }

  get auctionMarketConnectLink() {
    return get(this.auction, 'cr_pdf_link', '');
  }

  get auctionReleaseDate() {
    return get(this.auction, 'release_date', '');
  }
}
